if (!customElements.get('lookbook-icon')) {
  customElements.define(
    'lookbook-icon',
    class LookbookIcon extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          cardContainer: '.lookbook-card',
          cardProduct: '.lookbook-icon__card',
          cardsContainer: '.lookbook-card__cards-container',
          cardsInner: '.lookbook-card__cards-inner',
        };

        this._initBound = () => this.init();
        document.addEventListener('matchMobile', this._initBound);
        document.addEventListener('unmatchMobile', this._initBound);
        this.addEventListener('mouseover', this._initBound);
      }

      disconnectedCallback() {
        document.removeEventListener('matchMobile', this._initBound);
        document.removeEventListener('unmatchMobile', this._initBound);
        this.removeEventListener('mouseover', this._initBound);
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('mouseover', this.handleMouseOver);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
        this.cardContainer?.removeEventListener('mouseleave', this.handleContainerMouseLeave);
        this._clearHoverPending?.();
      }

      setupFixedPositionHandlers() {
        this.handleClick = this.handleClick.bind(this);
        this.handleMouseOver = this.handleMouseOver.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleContainerMouseLeave = this.handleContainerMouseLeave.bind(this);

        this.addEventListener('click', this.handleClick);
        this.addEventListener('mouseover', this.handleMouseOver);
        this.addEventListener('mouseleave', this.handleMouseLeave);
        this.cardContainer?.addEventListener('mouseleave', this.handleContainerMouseLeave);
      }

      showCardForIndex(index) {
        const container = this.cardContainer?.querySelector(this.selectors.cardsContainer);
        if (!container) return;
        const cards = container.querySelectorAll('.lookbook-hotspot-card');
        const targetIndex = String(index);
        cards.forEach((card) => {
          const cardIndex = card.getAttribute('data-hotspot-index');
          const isVisible = cardIndex === targetIndex;
          card.setAttribute('aria-hidden', !isVisible);
          card.dataset.visible = isVisible ? 'true' : 'false';
        });
      }

      handleClick() {
        if (!this.isFixedPosition) return;
        const cardIndex = this.dataset.cardIndex;
        if (cardIndex === undefined) return;

        this._clearHoverPending();
        const icons = this.cardContainer?.querySelectorAll('lookbook-icon');
        icons?.forEach((icon) => icon.classList.remove('is-active', 'is-current'));
        this.classList.add('is-active', 'is-current');
        this.showCardForIndex(cardIndex);
      }

      handleMouseOver(e) {
        if (!this.isFixedPosition) return;
        const cardIndex = this.dataset.cardIndex;
        if (cardIndex === undefined) return;
        const hoveredIcon = e.target?.closest?.('lookbook-icon');
        if (hoveredIcon !== this) return;

        this._clearHoverPending();
        const icons = this.cardContainer?.querySelectorAll('lookbook-icon');
        icons?.forEach((icon) => icon.classList.remove('is-active', 'is-current'));
        this.classList.add('is-active', 'is-current');
        this.showCardForIndex(cardIndex);
      }

      handleMouseLeave() {
        if (!this.isFixedPosition) return;
        this._clearHoverPending();
      }

      _clearHoverPending() {
        if (this._hoverPending) {
          clearTimeout(this._hoverPending);
          this._hoverPending = null;
        }
      }

      handleContainerMouseLeave() {
        if (!this.isFixedPosition || !this.cardsContainer) return;
        this.cardContainer?.querySelectorAll('lookbook-icon').forEach((icon) => icon._clearHoverPending?.());
        const activeIcon = this.cardContainer?.querySelector('lookbook-icon.is-active[data-card-index]');
        const icons = this.cardContainer?.querySelectorAll('lookbook-icon');
        icons?.forEach((icon) => icon.classList.remove('is-current'));
        if (activeIcon) {
          activeIcon.classList.add('is-current');
          this.showCardForIndex(activeIcon.dataset.cardIndex);
        }
      }

      init() {
        this.cardContainer = this.closest(this.selectors.cardContainer);
        this.cardsContainer = this.cardContainer?.querySelector(this.selectors.cardsContainer);
        this.isFixedPosition = this.cardContainer?.classList.contains('lookbook-card--fixed-position');

        if (this.isFixedPosition && this.cardsContainer) {
          const activeIcon = this.cardContainer?.querySelector('lookbook-icon.is-active[data-card-index]');
          if (activeIcon) {
            this.showCardForIndex(activeIcon.dataset.cardIndex);
          }
          return;
        }

        this.cardProduct = this.querySelector(this.selectors.cardProduct);
        if (!this.cardProduct) return;

        if (this.isFixedPosition && this.cardProduct) {
          this.cardContainer?.querySelectorAll('lookbook-icon').forEach((icon) => {
            if (icon.updateFixedCardPosition) icon.updateFixedCardPosition();
          });
          return;
        }

        this.cardContainerOffsetX = this.cardContainer.getBoundingClientRect().left;
        this.cardContainerOffsetY = this.cardContainer.getBoundingClientRect().top;

        this.offsetX = this.getBoundingClientRect().left - this.cardContainerOffsetX;
        this.offsetY = this.getBoundingClientRect().top - this.cardContainerOffsetY;

        this.width = this.clientWidth;
        this.cardWidth = this.cardProduct.clientWidth;
        this.cardHeight = this.cardProduct.clientHeight;
        this.wrapperWidth = this.cardContainer.clientWidth;
        this.wrapperHeight = this.cardContainer.clientHeight;

        this.cardPosition = this.offsetY > this.cardHeight ? 'top' : 'bottom';

        if (this.offsetX + this.width > this.cardWidth) {
          this.cardPosition += '-left';
        } else if (this.wrapperWidth - this.offsetX > this.cardWidth) {
          this.cardPosition += '-right';
        }

        this.dataset.position = this.cardPosition;
      }

      updateFixedCardPosition() {
        if (!this.cardProduct || !this.cardContainer) return;
        const rect = this.cardContainer.getBoundingClientRect();
        const iconRect = this.getBoundingClientRect();
        const padding = parseInt(this.cardContainer.dataset.cardPadding || '12', 10);
        const iconLeft = iconRect.left - rect.left;
        const iconTop = iconRect.top - rect.top;

        const position = window.matchMedia('(max-width: 767.98px)').matches
          ? this.cardContainer.dataset.cardPositionMobile || 'bottom-center'
          : this.cardContainer.dataset.cardPositionDesktop || 'top-right';

        let targetLeft = padding, targetTop = padding;
        const cardW = Math.max(this.cardProduct.offsetWidth || 280, 200);
        const cardH = Math.max(this.cardProduct.offsetHeight || 120, 80);
        const containerW = rect.width;
        const containerH = rect.height;

        if (position.includes('top')) targetTop = padding;
        else targetTop = containerH - cardH - padding;
        if (position.includes('left')) targetLeft = padding;
        else if (position.includes('right')) targetLeft = containerW - cardW - padding;
        else targetLeft = (containerW - cardW) / 2;

        this.cardProduct.style.left = (targetLeft - iconLeft) + 'px';
        this.cardProduct.style.top = (targetTop - iconTop) + 'px';
        this.cardProduct.style.right = 'auto';
        this.cardProduct.style.bottom = 'auto';
        this.cardProduct.style.transform = 'none';
        this.cardProduct.style.position = 'absolute';
      }

      connectedCallback() {
        this.cardContainer = this.closest(this.selectors.cardContainer);
        this.cardsContainer = this.cardContainer?.querySelector(this.selectors.cardsContainer);
        this.isFixedPosition = this.cardContainer?.classList.contains('lookbook-card--fixed-position');

        this.init();

        if (this.isFixedPosition && this.cardsContainer) {
          this.setupFixedPositionHandlers();
          const activeIcon = this.cardContainer?.querySelector('lookbook-icon.is-active[data-card-index]');
          if (activeIcon) this.showCardForIndex(activeIcon.dataset.cardIndex);
        } else if (this.isFixedPosition && this.querySelector(this.selectors.cardProduct)) {
          this.cardProduct = this.querySelector(this.selectors.cardProduct);
          this.setupFixedPositionHandlers();
        }

        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          this.cardProduct = this.querySelector(this.selectors.cardProduct);
          this.addEventListener('shopify:block:select', () => {
            if (this.cardProduct) this.cardProduct.style.transition = 'none';
            this.classList.add('is-active');
            if (this.cardContainer?.classList.contains('lookbook-card--fixed-position')) {
              this.classList.add('is-current');
              if (this.dataset.cardIndex !== undefined) this.showCardForIndex(this.dataset.cardIndex);
            }
          });
          this.addEventListener('shopify:block:deselect', () => {
            this.classList.remove('is-active', 'is-current');
          });
        }
      }
    }
  );
}
